import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Joi from 'joi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { useCreateNotificationMutation } from 'api/main';
import { appNotificationSelectors, userSelectors } from 'reducers';
import { useMatchAtLeastOneRole } from 'utils/hooks';
import { validateSchema, parseFullName } from 'utils/helpers';
import { quillFormats, RoleNames } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionButton, ErrorMessage, CustomDialogTitle, CustomTextField } from 'components';
import { DialogContent, DialogActions, Dialog, Typography } from '@mui/material';

import { notificationDialogClosed } from 'reducers/appNotification';

const NotificationDialog = () => {
    const { open, receiver } = useSelector(appNotificationSelectors.getNotificationDialogStatus);
    const dispatch = useDispatch();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const distributorLogoURL = useSelector(userSelectors.getDistributorLogoUrl);

    const [createNotification, { isLoading: loading, isSuccess, reset }] =
        useCreateNotificationMutation();

    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
    ]);
    const companyName = useMemo(
        () => internalUser?.distributorCompany?.corporateName || '',
        [internalUser]
    );

    const sender = useMemo(() => {
        if (userIsDistributor) {
            return companyName;
        } else {
            return texts.PE_TECH;
        }
    }, [userIsDistributor, companyName]);

    const defaultMessage = useMemo(
        () =>
            `${receiver?.title || texts.CHERE} ${parseFullName(
                receiver
            )},</br></br>Cordialement,</br>${sender}`,
        [receiver, sender]
    );

    const [subject, setSubject] = useState('');
    const [editorValue, setEditorValue] = useState(defaultMessage + ``);

    const [errors, setErrors] = useState({
        subject: '',
        editorValue: '',
    });

    const schema = useMemo(
        () => ({
            subject: Joi.string()
                .required()
                .messages({ 'string.empty': texts.VEUILLES_SAISIR_UN_SUJET }),
            editorValue: Joi.string()
                .required()
                .invalid('<p><br></p>', defaultMessage)
                .messages({ 'string.base': texts.VEUILLEZ_SAISIR_LE_CONTENU }),
        }),
        [defaultMessage]
    );

    const resetNotificationContent = useCallback(() => {
        reset();
        setEditorValue(defaultMessage);
        setSubject('');

        setErrors({ subject: '', editorValue: '' });
    }, [defaultMessage, reset]);

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, { subject, editorValue });
        setErrors(newErrors);

        return valid;
    }, [schema, subject, editorValue]);

    const handleSendNotification = useCallback(() => {
        if (formIsValid()) {
            createNotification({
                receiver: receiver?.id,
                subject,
                messageContent: editorValue,
                senderName: sender,
                userIsDistributor,
                distributorLogoURL,
            });
        }
    }, [
        formIsValid,
        editorValue,
        subject,
        createNotification,
        receiver,
        sender,
        userIsDistributor,
        distributorLogoURL,
    ]);

    const onClosePopUp = useCallback(() => {
        dispatch(notificationDialogClosed());
        resetNotificationContent();
    }, [resetNotificationContent, dispatch]);

    const handleChange = useCallback(e => {
        setSubject(e.target.value);
    }, []);

    useEffect(() => {
        if (isSuccess) {
            onClosePopUp();
        }
    }, [isSuccess, onClosePopUp]);

    useEffect(() => {
        if (defaultMessage) setEditorValue(defaultMessage);
    }, [defaultMessage]);

    if (!receiver) return null;

    return (
        <Dialog open={open} maxWidth="md" fullWidth>
            <CustomDialogTitle title={texts.ENVOYER_UN_MESSAGE} handleClose={onClosePopUp} />
            <DialogContent sx={theme => ({ padding: theme.spacing(2, 6) })}>
                <CustomTextField
                    fullWidth
                    id={`subject-${receiver.id}`}
                    label={texts.SUBJECT}
                    value={subject}
                    onChange={handleChange}
                    error={errors.subject}
                />
                <Typography gutterBottom align="left" variant="body2" display="block">
                    {texts.VOTRE_MESSAGE}
                </Typography>
                <ReactQuill value={editorValue} onChange={setEditorValue} formats={quillFormats} />
            </DialogContent>
            {errors.editorValue && (
                <ErrorMessage
                    gutterBottom
                    variant="caption"
                    align="center"
                    message={texts.VEUILLEZ_SAISIR_LE_CONTENU}
                />
            )}
            <DialogActions>
                <ActionButton loading={loading} disabled={loading} onClick={handleSendNotification}>
                    {texts.ENVOYER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default NotificationDialog;
